import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { useDashboardVendedor } from './hooks/useDashboard.js';
import { KpiCard } from './components/KpiCard.js';
import { AlertCard } from './components/AlertCard.js';
import { PeriodoSelector } from './components/PeriodoSelector.js';
import { VendasHistoricoChart } from './components/VendasHistoricoChart.js';
import { VendasDiaChart } from './components/VendasDiaChart.js';
import { TopClientesChart } from './components/TopClientesChart.js';
import { BrasilMapaChart } from './components/BrasilMapaChart.js';
import { DashboardErrorBoundary } from './components/DashboardErrorBoundary.js';
import type { DashboardPeriodo } from './hooks/useDashboard.js';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// AIDEV-NOTE: Dashboard do vendedor — KPIs, visitas, 3 gráficos Recharts, filtro mês/ano
export function DashboardVendedor() {
  const agora = new Date();
  const [periodo, setPeriodo] = useState<DashboardPeriodo>({
    mes: agora.getMonth() + 1,
    ano: agora.getFullYear()
  });

  const { data, isLoading, error } = useDashboardVendedor(periodo);

  const mesLabel = `${MESES[periodo.mes - 1]}/${periodo.ano}`;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Erro ao carregar dashboard do vendedor.
      </Typography>
    );
  }

  const visitasKpi = {
    label: 'Visitas no mês',
    value: data.visitasMes,
    unit: 'number' as const
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Cabeçalho com seletor de período */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Resultados de {mesLabel}
          </Typography>
        </Box>
        <PeriodoSelector periodo={periodo} onChange={setPeriodo} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {data.kpis.map((kpi) => (
          <Grid item xs={6} sm={4} md={3} key={kpi.label}>
            <DashboardErrorBoundary>
              <KpiCard metric={kpi} />
            </DashboardErrorBoundary>
          </Grid>
        ))}
        <Grid item xs={6} sm={4} md={3}>
          <DashboardErrorBoundary>
            <KpiCard metric={visitasKpi} />
          </DashboardErrorBoundary>
        </Grid>
      </Grid>

      {/* Meta mensal */}
      {data.goalPercent !== null && data.goalPercent !== undefined && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Meta Mensal
                </Typography>
                <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(data.goalPercent, 100)}%`,
                      bgcolor:
                        data.goalPercent >= 100
                          ? 'success.main'
                          : data.goalPercent >= 80
                            ? 'primary.main'
                            : 'warning.main',
                      borderRadius: 4,
                      transition: 'width 0.5s ease'
                    }}
                  />
                </Box>
              </Box>
              <Chip
                label={`${data.goalPercent}%`}
                color={
                  data.goalPercent >= 100
                    ? 'success'
                    : data.goalPercent >= 80
                      ? 'primary'
                      : 'warning'
                }
                sx={{ fontWeight: 700, minWidth: 64 }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Gráficos */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar gráfico de histórico">
            <VendasHistoricoChart dados={data.vendasHistorico} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar gráfico diário">
            <VendasDiaChart dados={data.vendasDia} mesLabel={mesLabel} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar top clientes">
            <TopClientesChart dados={data.topClientes} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar mapa de vendas">
            <BrasilMapaChart periodo={periodo} />
          </DashboardErrorBoundary>
        </Grid>
      </Grid>

      {/* Alertas */}
      {data.alertas.length > 0 && (
        <DashboardErrorBoundary fallbackMessage="Erro ao renderizar alertas">
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Alertas
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <AlertCard alertas={data.alertas} />
            </CardContent>
          </Card>
        </DashboardErrorBoundary>
      )}
    </Box>
  );
}
